<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMessaging } from '~/composables/useMessaging';
import ConversationList from './ConversationList.vue';
import MessageThread from './MessageThread.vue';
import NewConversationDialog from './NewConversationDialog.vue';
import {apiMessage} from '~/composables/useSafety';

const route = useRoute();
const router = useRouter();

const {
  conversations,
  activeConversation,
  activeConversationId,
  activeMessages,
  typingUsers,
  syncInProgress,
  initialize,
  syncMessages,
  sendMessage,
  sendImageMessage,
  setActiveConversation,
  createConversation,
  deleteConversation,
  updateConversationTitle,
  updateConversationParticipants,
  updateConversationAdmins,
  leaveConversation,
  setConversationNotifications,
  deleteFailedMessage,
  deleteSentMessage,
  retrySendMessage,
  sendTypingIndicator
} = useMessaging();

const {setAlertMessage} = useAlertService();
const showNewConversationDialog = ref(false);
const showMobileThread = ref(false);

// Get typing users for a conversation
const getTypingUsers = (conversationId) => {
  if (!conversationId) return new Set();
  return typingUsers.value.get(conversationId) || new Set();
};

// Handle conversation selection
const handleSelectConversation = async (conversation) => {
  await setActiveConversation(conversation.id);
  showMobileThread.value = true;

  // Update URL with conversationId query param
  if (route.query.conversationId !== conversation.id) {
    router.push({
      query: { ...route.query, conversationId: conversation.id }
    });
  }
};

// Handle send message
//
// The refusal HAS to reach the screen. It did not: sendMessage rethrows and
// nothing here caught it, so blocking someone and then writing to them showed
// no error at all, the composer cleared as though it had sent, and the chat
// list went on showing the message as the latest one. Measured on 19 Sep 2026 —
// the server answered 400 "You can't message this person." and the sender was
// told nothing.
const handleSendMessage = async (text) => {
  try {
    await sendMessage(text);
  } catch (e) {
    setAlertMessage(apiMessage(e, "Couldn't send that message. Please try again."));
  }
};

// Handle send image message
const handleSendImageMessage = async (imageFile, caption) => {
  try {
    await sendImageMessage(activeConversationId.value, imageFile, caption);
  } catch (e) {
    setAlertMessage(apiMessage(e, "Couldn't send that image. Please try again."));
  }
};

// Handle retry failed message
const handleRetryMessage = async (message) => {
  try {
    await retrySendMessage(message);
  } catch (error) {
    console.error('Failed to retry message:', error);
  }
};

// Handle delete failed message
const handleDeleteMessage = async (message) => {
  console.log('handleDeleteMessage called with message:', message);
  console.log('Message ID:', message.id);
  await deleteFailedMessage(message.id);
  console.log('deleteFailedMessage completed');
};

// Delete my own sent message, for everyone (24 Sep 2026). The placeholder
// shows at once; a refusal or no connection puts it back and says why.
const handleDeleteSentMessage = async (message) => {
  const result = await deleteSentMessage(message);
  if (!result.ok && result.error) setAlertMessage(result.error);
};

// Handle typing indicator
const handleTyping = (isTyping) => {
  sendTypingIndicator(isTyping);
};

// Handle back to list (mobile)
const handleBackToList = () => {
  showMobileThread.value = false;
  // Remove conversationId from URL
  const { conversationId, ...restQuery } = route.query;
  router.push({ query: restQuery });
};

// Handle conversation created (from dialog)
const handleConversationCreated = (conversation) => {
  // Show the new conversation in mobile view
  showMobileThread.value = true;
};

// Handle delete conversation
const handleDeleteConversation = async (conversation) => {
  try {
    await deleteConversation(conversation.id);
  } catch (error) {
    console.error('Failed to delete conversation:', error);
  }
};

// Handle update conversation participants
// A refused add used to reach console.error and nothing else, so the person
// simply was not added and was told nothing. The server now refuses an add that
// breaks the shared-club rule or a block, and it words the refusal itself
// ("You can only message someone who is in one of your clubs." / "You can't
// message this person."), so show that rather than judging it again here.
const handleUpdateParticipants = async (participantIds) => {
  try {
    await updateConversationParticipants(activeConversationId.value, participantIds);
  } catch (error) {
    console.error('Failed to update participants:', error);
    setAlertMessage(apiMessage(error, "Couldn't change who is in this chat. Please try again."));
  }
};

// Handle update conversation admins
const handleUpdateAdmins = async (adminIds) => {
  try {
    await updateConversationAdmins(activeConversationId.value, adminIds);
  } catch (error) {
    console.error('Failed to update admins:', error);
  }
};

// Handle update conversation title
const handleUpdateTitle = async (newTitle) => {
  try {
    await updateConversationTitle(activeConversationId.value, newTitle);
  } catch (error) {
    console.error('Failed to update conversation title:', error);
  }
};

// Handle leave conversation
const handleLeaveConversation = async () => {
  try {
    await leaveConversation(activeConversationId.value);
    // Navigate back to list on mobile
    showMobileThread.value = false;
    // Remove conversationId from URL
    const { conversationId, ...restQuery } = route.query;
    router.push({ query: restQuery });
  } catch (error) {
    console.error('Failed to leave conversation:', error);
  }
};

// Handle update notification preferences
const handleUpdateNotifications = async (notificationsEnabled) => {
  try {
    await setConversationNotifications(activeConversationId.value, notificationsEnabled);
  } catch (error) {
    console.error('Failed to update notification preferences:', error);
  }
};

// Initialize on mount
onMounted(async () => {
  await initialize();

  // Check if conversationId in URL
  if (route.query.conversationId) {
    await setActiveConversation(route.query.conversationId);
    showMobileThread.value = true;
  }
});

// Watch for conversationId changes in URL
watch(() => route.query.conversationId, async (conversationId) => {
  if (conversationId && conversationId !== activeConversationId.value) {
    await setActiveConversation(conversationId);
    showMobileThread.value = true;
  } else if (!conversationId && activeConversationId.value) {
    await setActiveConversation(null);
    showMobileThread.value = false;
  }
});
</script>

<template>
  <div class="messaging-view chat-split">
    <!-- Conversations list pane -->
    <div class="chat-list-pane" :class="{ 'pane-hidden-mobile': showMobileThread }">
      <ConversationList
        :conversations="conversations"
        :active-conversation-id="activeConversationId"
        :sync-in-progress="syncInProgress"
        @select-conversation="handleSelectConversation"
        @new-conversation="showNewConversationDialog = true"
        @sync="syncMessages"
        @delete="handleDeleteConversation"
      />
    </div>

    <!-- Message thread pane -->
    <div class="chat-thread-pane" :class="{ 'pane-hidden-mobile': !showMobileThread }">
      <MessageThread
        :conversation="activeConversation"
        :messages="activeMessages"
        :typing-users="getTypingUsers(activeConversationId)"
        @send="handleSendMessage"
        @send-image="handleSendImageMessage"
        @retry="handleRetryMessage"
        @delete="handleDeleteMessage"
        @delete-for-everyone="handleDeleteSentMessage"
        @typing="handleTyping"
        @back="handleBackToList"
        @update-participants="handleUpdateParticipants"
        @update-admins="handleUpdateAdmins"
        @update-title="handleUpdateTitle"
        @leave="handleLeaveConversation"
        @update-notifications="handleUpdateNotifications"
      />
    </div>

    <!-- New conversation dialog -->
    <NewConversationDialog
      v-model="showNewConversationDialog"
      @created="handleConversationCreated"
    />
  </div>
</template>

<style scoped lang="scss">
.chat-split {
  display: grid;
  grid-template-columns: 1fr;
  height: 100%;
  min-height: 0;
}

.chat-list-pane,
.chat-thread-pane {
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@media (min-width: 960px) {
  .chat-split {
    grid-template-columns: 340px 1fr;
  }

  .chat-list-pane {
    border-right: 1px solid rgb(var(--v-theme-borderLight));
  }
}

@media (max-width: 959.98px) {
  .pane-hidden-mobile {
    display: none;
  }
}
</style>
